import React, { useState, useRef } from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { UPLOAD_URL } from '../config';

interface ImageUploaderProps {
  /** Current image URL (from DB or state) */
  value: string;
  /** Called with the new backend-relative path after a successful upload */
  onChange: (path: string) => void;
  /** Subfolder inside backend/uploads/ (e.g. "projects", "sliders") */
  folder?: string;
  /** Label shown above the uploader */
  label?: string;
  /** Extra Tailwind classes for the wrapper */
  className?: string;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  value,
  onChange,
  folder = 'general',
  label = 'Image',
  className = '',
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setError('');
    setUploading(true);

    const formData = new FormData();
    formData.append('image', file);
    formData.append('folder', folder);

    try {
      const res = await fetch(UPLOAD_URL, {
        method: 'POST',
        credentials: 'include',   // send httpOnly admin_token cookie
        body: formData,
      });
      const json = await res.json();

      if (json.status === 'success') {
        onChange(json.path);
      } else {
        setError(json.message || 'Upload failed.');
      }
    } catch {
      setError('Network error. Could not upload image.');
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const clearImage = () => {
    onChange('');
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <label className="block text-xs font-bold text-gray-500 uppercase">{label}</label>}

      {/* Preview */}
      {value && (
        <div className="relative inline-block">
          <img
            src={value}
            alt="Preview"
            className="h-32 w-auto rounded-lg border border-gray-200 object-cover shadow-sm"
            onError={(e) => { (e.target as HTMLImageElement).src = ''; }}
          />
          <button
            type="button"
            onClick={clearImage}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 shadow"
            title="Remove image"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => inputRef.current?.click()}
        className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:border-[#EF5D22] hover:bg-orange-50 transition-colors"
      >
        {uploading ? (
          <div className="flex items-center space-x-2 text-[#EF5D22]">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <span className="text-sm font-medium">Uploading…</span>
          </div>
        ) : (
          <>
            <PhotoIcon className="w-8 h-8 text-gray-400 mb-1" />
            <p className="text-sm text-gray-500">
              <span className="font-semibold text-[#EF5D22]">Click to upload</span> or drag &amp; drop
            </p>
            <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, WebP, GIF, SVG · max 10 MB</p>
          </>
        )}
      </div>

      {/* Manual URL input as fallback */}
      <input
        type="text"
        placeholder="…or paste an image URL"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full p-2 border rounded text-sm text-gray-600 focus:ring-[#EF5D22] focus:border-[#EF5D22] outline-none"
      />

      {error && <p className="text-xs text-red-500">{error}</p>}

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        title="Upload image file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={handleInputChange}
      />
    </div>
  );
};

export default ImageUploader;
